// @ts-nocheck
import { callApi } from 'services/api/api'
import { toastStore } from 'stores/toast'
import { TOAST_TYPE } from 'utils/constants'
import { baseURL, isLocalEnv } from 'services/api/apiConfig'
import { appSelected } from 'stores/navigation'

let selectedApplication = 'physician'
appSelected.subscribe(value => {
  if (value.toLocaleLowerCase() === 'app') {
    selectedApplication = 'app'
  } else {
    selectedApplication = 'physician'
  }
})

/**
 * Gets the survey data based on the provided parameters.
 * @param {object} data - The data object containing the necessary parameters.
 * @param {string} reportType - The report type.
 * @returns {Promise<any>} - A promise that resolves to the survey data.
 */
export async function getSurveyData(data, reportType) {
  let url = ''
  if (reportType === 'specialty') {
    url = `${baseURL}/surveys/reports/specialty/${selectedApplication}`
  } else if (reportType === 'datavariable') {
    url = `${baseURL}/surveys/reports/datavariable/${selectedApplication}`
  }
  return new Promise((resolve, reject) => {
    callApi(url, 'POST', data)
      .then(data => {
        resolve(data)
      })
      .catch(error => {
        console.log(error)
        toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error retrieving Survey Data.')
        reject(error)
      })
  })
}

export function isHeaderBasedSurveySelection({ surveyKey, marketDataHeaderKey, marketDataHeaderType } = {}) {
  const normalizedType = marketDataHeaderType?.toLowerCase?.() || ''
  return !surveyKey && !!marketDataHeaderKey && ['blends', 'uploads'].includes(normalizedType)
}

export const getDataVariables = async (surveyKey, cutKey, marketDataHeaderKey, marketDataHeaderCutKey, marketDataHeaderType) => {
  try {
    let url = buildUrlWithSurveyOrMarketKey(
      `${baseURL}/survey-report/${selectedApplication}/data-variables`,
      surveyKey,
      cutKey,
      marketDataHeaderKey,
      marketDataHeaderCutKey,
      marketDataHeaderType
    )
    const response = await callApi(url, 'GET')
    return response
  } catch (error) {
    toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error retrieving my data variables. Please try again later.')
    return []
  }
}

export const getSpecialties = async (
  surveyKey,
  cutKey,
  marketDataHeaderKey,
  marketDataHeaderCutKey,
  marketDataHeaderType,
  specialtyGroupNames = []
) => {
  try {
    const data = buildSpecialtiesRequestPayload(
      surveyKey,
      cutKey,
      marketDataHeaderKey,
      marketDataHeaderCutKey,
      marketDataHeaderType,
      specialtyGroupNames
    )

    return callApi(`${baseURL}/survey-report/${selectedApplication}/specialties`, 'POST', data)
  } catch (error) {
    toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error getting my specialties')
    console.error(error)
    return []
  }
}

export const getDataSpecialtyGroups = async (surveyKey, cutKey, marketDataHeaderKey, marketDataHeaderCutKey, marketDataHeaderType) => {
  try {
    const url = buildUrlWithSurveyOrMarketKey(
      `${baseURL}/survey-report/${selectedApplication}/specialty-groups`,
      surveyKey,
      cutKey,
      marketDataHeaderKey,
      marketDataHeaderCutKey,
      marketDataHeaderType
    )
    const response = await callApi(url, 'GET')
    return response
  } catch (error) {
    toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error retrieving my specialty groups. Please try again later.')
    return []
  }
}

/**
 * Gets the list of available Survey Characteristic based on the survey key.
 * @param {number} surveyKey
 * @returns {Promise<any>}
 */
export const getSurveyCutsMarketLibrary = async surveyKey => {
  if (!surveyKey) {
    toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error getting survey characteristics')
    return []
  }

  return new Promise(resolve => {
    callApi(`${baseURL}/surveys/org-characteristics?surveyKey=${surveyKey}`)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error getting survey characteristics')
        console.log(error)
        resolve([])
      })
  })
}

export const getSurveyMarketLibrary = async () => {
  return new Promise((resolve, reject) => {
    callApi(`${baseURL}/surveys/market-library/${selectedApplication}`)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        if (isLocalEnv) {
          toastStore.createToast(TOAST_TYPE.ERROR, 'Mocked data', 'We are mocking data for getSurveyMarketLibrary')
        } else {
          toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error getting market library')
          reject(error)
          console.error(error)
        }
      })
  })
}

export const getSurveyMarketLibraryManageData = async (showDefault = true) => {
  return new Promise((resolve, reject) => {
    callApi(`${baseURL}/surveys/market-library/${selectedApplication}?showDefault=${showDefault}`)
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        if (isLocalEnv) {
          toastStore.createToast(TOAST_TYPE.ERROR, 'Mocked data', 'We are mocking data for getSurveyMarketLibrary')
        } else {
          toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error getting market library')
          reject(error)
          console.error(error)
        }
      })
  })
}

/**
 * Saves the blend market data
 * @param {object} body - The data object containing the necessary parameters.
 * @param {string} body.ageTcc - The age TCC value.
 * @param {Array} body.blendSurveys - The array of blend surveys.
 * @param {number} body.blendSurveys[].cut_key - The cut key for the survey.
 * @param {number} body.blendSurveys[].survey_key - The survey key.
 * @param {number} body.blendSurveys[].surveyYear - The survey year.
 * @param {Array} body.blendMarketDataUploads - The array of upload market data selections.
 * @param {number} body.blendMarketDataUploads[].market_data_header_key - The upload header key.
 * @param {number} body.blendMarketDataUploads[].market_data_header_cut_key - The upload cut key.
 * @param {string} body.name - The name.
 */
export const saveBlendMarketData = async body => {
  // eslint-disable-next-line no-useless-catch
  try {
    return await callApi(`${baseURL}/surveys/blend/${selectedApplication}`, 'POST', body)
  } catch (error) {
    throw error
  }
}

export async function getSurveyReportExport(data, reportType) {
  let url = ''
  if (reportType === 'specialty') {
    url = `${baseURL}/surveys/reports/specialty/${selectedApplication}/export`
  } else if (reportType === 'datavariable') {
    url = `${baseURL}/surveys/reports/datavariable/${selectedApplication}/export`
  }

  try {
    return await callApi(url, 'POST', data)
  } catch (error) {
    console.log(error)
    toastStore.createToast(TOAST_TYPE.ERROR, 'Server error', 'Error exporting Survey Data.')
  }
}

export function buildSpecialtiesRequestPayload(
  surveyKey,
  cutKey,
  marketDataHeaderKey,
  marketDataHeaderCutKey,
  marketDataHeaderType,
  specialtyGroupNames = []
) {
  const usesMarketDataHeader = isHeaderBasedSurveySelection({ surveyKey, marketDataHeaderKey, marketDataHeaderType })
  const data = {
    specialty_group_names: specialtyGroupNames,
  }

  if (usesMarketDataHeader) {
    data.market_data_header_key = marketDataHeaderKey
    data.market_data_header_type = marketDataHeaderType

    if (marketDataHeaderCutKey) {
      data.market_data_header_cut_key = marketDataHeaderCutKey
    }
  } else {
    data.survey_key = surveyKey

    if (cutKey) {
      data.cut_key = cutKey
    }
  }

  return data
}

export function buildUrlWithSurveyOrMarketKey(baseUrl, surveyKey, cutKey, marketDataHeaderKey, marketDataHeaderCutKey, marketDataHeaderType) {
  const url = new URL(baseUrl)
  const usesMarketDataHeader = isHeaderBasedSurveySelection({ surveyKey, marketDataHeaderKey, marketDataHeaderType })

  if (usesMarketDataHeader) {
    url.searchParams.set('marketDataHeaderKey', marketDataHeaderKey)
    if (marketDataHeaderCutKey) {
      url.searchParams.set('marketDataHeaderCutKey', marketDataHeaderCutKey)
    }
    if (marketDataHeaderType) {
      url.searchParams.set('marketDataHeaderType', marketDataHeaderType)
    }
  } else if (surveyKey) {
    url.searchParams.set('surveyKey', surveyKey)
    if (cutKey) {
      url.searchParams.set('cutKey', cutKey)
    }
  }

  return url.toString()
}
